import PropTypes from 'prop-types';

import Checkbox from '#globalComponents/Checkbox/Checkbox';
import FormField from '#globalComponents/Forms/FormField';
import TextInput from '#globalComponents/Forms/TextInput';
import Panel from '#globalComponents/Overlay/Panel';
import RadioButton from '#globalComponents/RadioButton/RadioButton';
import RolePriorityPicker from '#globalComponents/RolePriorityPicker/RolePriorityPicker';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import useContactRoles from '#globalHooks/useContactRoles';
import PhoneRevealOption from '#leadOverviewFeature/components/LeadOverviewPage/LeadOverviewContainer/SubComponents/ContactEnrichmentModal/PhoneRevealOption';
import { PHONE_ADDON } from '#lib/addons/addonConfigs';

import { CONTACT_MODES, MAX_PER_COMPANY } from '../../constants/conversation';

const SectionLabel = ({ children }) => (
	<p className="mb-1 text-xs font-medium uppercase tracking-wide text-bgBase-800">{children}</p>
);

SectionLabel.propTypes = { children: PropTypes.node.isRequired };

/**
 * Which contacts per company and which data: the four modes, the role priority of the existing
 * enrichment dialog, a per-company cap for "all", e-mail always, phone with the add-on, the
 * postal address free.
 */
const ContactsPopover = ({ value, onChange }) => {
	const { hasCapability: hasPhoneAddon } = useCapabilityCheck(`${PHONE_ADDON.key}.access`);
	const { enrichableRoles } = useContactRoles();
	const withContacts = value.contactMode !== 'none';

	return (
		<Panel.Body className="space-y-4">
			<section>
				<SectionLabel>Kontakte je Firma</SectionLabel>
				<div className="space-y-0.5">
					{CONTACT_MODES.map((mode) => (
						<RadioButton
							key={mode.key}
							name="agent-contact-mode"
							value={mode.key}
							checked={value.contactMode === mode.key}
							onChange={(next) => onChange({ contactMode: next })}
							label={mode.label}
							size="sm"
						/>
					))}
				</div>
				{value.contactMode === 'roles' && (
					<div className="pl-6 pt-2">
						<RolePriorityPicker
							value={value.roles}
							onChange={(roles) => onChange({ roles })}
							options={enrichableRoles}
						/>
					</div>
				)}
				{value.contactMode === 'all' && (
					<div className="pl-6 pt-3">
						<FormField
							label="Maximal pro Firma"
							htmlFor="agent-max-contacts"
							hint={`${MAX_PER_COMPANY.min} bis ${MAX_PER_COMPANY.max}, bereits vorhandene zählen mit`}
						>
							<TextInput
								id="agent-max-contacts"
								type="number"
								min={MAX_PER_COMPANY.min}
								max={MAX_PER_COMPANY.max}
								value={value.maxPerCompany}
								onChange={(e) => onChange({ maxPerCompany: Number(e.target.value) })}
								className="max-w-[8rem]"
							/>
						</FormField>
					</div>
				)}
			</section>
			{withContacts && (
				<section className="space-y-2">
					<SectionLabel>Daten je Kontakt</SectionLabel>
					<Checkbox id="agent-data-email" checked disabled label="E-Mail (immer enthalten)" />
					<PhoneRevealOption
						checked={value.phone}
						onChange={(phone) => onChange({ phone })}
						hasPhoneAddon={hasPhoneAddon}
					/>
					<Checkbox id="agent-data-address" checked disabled label="Postadresse der Firma (kostenlos)" />
					<p className="text-xs text-bgBase-800">Ein Kontakt-Credit je neu gefundenem Ansprechpartner.</p>
				</section>
			)}
		</Panel.Body>
	);
};

ContactsPopover.propTypes = {
	value: PropTypes.shape({
		contactMode: PropTypes.string.isRequired,
		roles: PropTypes.arrayOf(PropTypes.string).isRequired,
		phone: PropTypes.bool.isRequired,
		maxPerCompany: PropTypes.number.isRequired,
	}).isRequired,
	onChange: PropTypes.func.isRequired,
};

export default ContactsPopover;
